import ConfigLoader from "@/components/ConfigLoader";
import ThemeProvider from "@/components/ThemeProvider";
import EstorePopup from "@/components/EstorePopup";
import { AuthProvider } from "@/context/AuthContext";
import { ConfigProvider, useConfig } from "@/context/ConfigContext";
import { DeviceProvider, useDevice } from "@/context/DeviceContext";
import {
  getFacebookPixelId,
  getGoogleAnalyticsId,
  getGoogleSiteVerification,
  getBingSiteVerification,
  getYandexSiteVerification,
  getSiteName,
  getSiteTitle,
} from "@/utils/configHelper";
import { GoogleAnalytics } from "@next/third-parties/google";
import axios from "axios";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Head from "next/head";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "../styles/globals.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export const dynamic = "force-static";
const queryClient = new QueryClient();

// Component untuk menggunakan config di dalam App
function AppContent({ Component, pageProps }) {
  const router = useRouter();
  const { config, isConfigReady } = useConfig();
  const { ready: deviceReady } = useDevice();

  const isFloatingChatActive = config?.is_floating_chat_active === true;
  const chatbotId = process.env.NEXT_PUBLIC_CHATBOT_ID || "";

  // Dapatkan analytics IDs dari config
  const googleAnalyticsId = config
    ? getGoogleAnalyticsId(config)
    : null; // fallback default
  const facebookPixelId = config
    ? getFacebookPixelId(config)
    : null; // fallback default
  const googleSiteVerification = config
    ? getGoogleSiteVerification(config)
    : null;
  const bingSiteVerification = config
    ? getBingSiteVerification(config)
    : null;
  const yandexSiteVerification = config
    ? getYandexSiteVerification(config)
    : null;

  // Dapatkan site title dari config
  const siteName = config ? getSiteName(config) : "BaseStore";
  // Use site.name for browser tab title (more descriptive than site.title slug)
  const siteTitle = siteName;
  // Dynamic favicon: use configured favicon or generate letter-based SVG from site name
  const faviconHref = config?.logo?.favicon
    ? config.logo.favicon
    : siteName
    ? `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>${encodeURIComponent(siteName.charAt(0).toUpperCase())}</text></svg>`
    : "/favicon.ico";

  // Catat affiliate code dari utm_affiliate query param
  useEffect(() => {
    if (!router.isReady) return;
    if (!deviceReady) return;
    const code = router.query?.utm_affiliate;
    if (!code) return;
    axios.post("/api/device-id/affiliate", { affiliate_code: code }).catch(() => {});
  }, [router.isReady, deviceReady, router.query?.utm_affiliate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page analytics — fire setiap halaman dibuka/berganti
  useEffect(() => {
    if (!isConfigReady) return;

    const trackPage = (url) => {
      const path = url.split("?")[0]; // buang query string
      fetch("/api/analytics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: path }),
      }).catch(() => {}); // gagal diam-diam
    };

    // Initial load
    trackPage(router.asPath);

    // Setiap route change
    router.events.on("routeChangeComplete", trackPage);
    return () => router.events.off("routeChangeComplete", trackPage);
  }, [isConfigReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isConfigReady && facebookPixelId) {
      import("react-facebook-pixel")
        .then((x) => x.default)
        .then((ReactPixel) => {
          ReactPixel.init(facebookPixelId);
          ReactPixel.pageView();

          router.events.on("routeChangeComplete", () => {
            ReactPixel.pageView();
          });
        });
    }
  }, [router.events, isConfigReady, facebookPixelId]);

  return (
    <>
      <Head>
        <title>{siteTitle}</title>
        <meta name="application-name" content={siteName} />
        {googleSiteVerification && (
          <meta
            name="google-site-verification"
            content={googleSiteVerification}
          />
        )}
        {bingSiteVerification && (
          <meta name="msvalidate.01" content={bingSiteVerification} />
        )}
        {yandexSiteVerification && (
          <meta
            name="yandex-verification"
            content={yandexSiteVerification}
          />
        )}
        <link rel="icon" href={faviconHref} />
      </Head>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <main id="main-content">
              <Component {...pageProps} />
            </main>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
      <EstorePopup />
      {googleAnalyticsId && (
        <GoogleAnalytics gaId={googleAnalyticsId}></GoogleAnalytics>
      )}
      <ToastContainer />
      {isFloatingChatActive && chatbotId && (
        <Script
          src="https://chatbot.tukuya.com/widget.js"
          data-chatbot-id={chatbotId}
          strategy="lazyOnload"
        />
      )}
    </>
  );
}

export default function App({ Component, pageProps }) {
  return (
    <DeviceProvider>
      <ConfigProvider>
        <ConfigLoader>
          <AppContent Component={Component} pageProps={pageProps} />
        </ConfigLoader>
      </ConfigProvider>
    </DeviceProvider>
  );
}
